// components/cookies/CookieConsentModal.jsx
"use client";

import { useEffect, useRef, useState } from "react";

export default function CookieConsentModal({
    defaultValues,
    onAcceptAll,
    onRejectAll,
    onSave,
    onClose,
    onRemindLater, // <— NEW
    privacyHref = "/privacy-policy",
    termsHref = "/terms",
    showRemindLater = false, // NEW
}) {
    const [open, setOpen] = useState(true);
    const [showCustom, setShowCustom] = useState(false);

    // toggle states (seeded from props)
    const [analytics, setAnalytics] = useState(!!defaultValues?.analytics);
    const [personalization, setPersonalization] = useState(
        !!defaultValues?.personalization
    );
    const [ads, setAds] = useState(!!defaultValues?.ads);
    const [preferences, setPreferences] = useState(
        !!defaultValues?.preferences
    );

    const dialogRef = useRef(null);

    // keep state in sync if defaults change
    useEffect(() => {
        if (!defaultValues) return;
        setAnalytics(!!defaultValues.analytics);
        setPersonalization(!!defaultValues.personalization);
        setAds(!!defaultValues.ads);
        setPreferences(!!defaultValues.preferences);
    }, [defaultValues]);

    // lock background scroll
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => (document.body.style.overflow = prev);
    }, [open]);

    // focus + ESC to close
    useEffect(() => {
        dialogRef.current?.focus?.();
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                handleClose();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    if (!open) return null;

    const handleAcceptAll = () => {
        onAcceptAll?.();
        setOpen(false);
    };
    const handleSave = () => {
        onSave?.({ analytics, personalization, ads, preferences });
        setOpen(false);
    };
    const handleRejectAll = () => {
        onRejectAll?.();
        setOpen(false);
    };
    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const handleRemindLater = () => {
        onRemindLater?.(); // sets cooldown + closes in provider
        setOpen(false);
    };

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-title"
            aria-describedby="cookie-desc"
        >
            <div
                ref={dialogRef}
                tabIndex={-1}
                className="w-full max-w-2xl rounded-lg bg-white dark:bg-zinc-900 shadow-lg outline-none"
            >
                {/* Header / Copy */}
                <div className="px-4 sm:px-6 pt-5 pb-3 relative">
                    {/* Close button */}
                    <button
                        type="button"
                        aria-label="Close cookie dialog"
                        onClick={
                            showRemindLater ? handleRemindLater : handleClose
                        }
                        className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-zinc-800"
                    >
                        <span aria-hidden>✕</span>
                    </button>

                    <h2 id="cookie-title" className="text-lg font-semibold">
                        We use cookies to make things work better for you
                    </h2>
                    <p
                        id="cookie-desc"
                        className="mt-2 text-sm text-neutral-700 dark:text-neutral-300"
                    >
                        We use essential cookies to keep the site secure and
                        reliable. With your permission, we’d also like to use
                        analytics to understand what’s working, personalization
                        to tailor content, and advertising technologies to fund
                        our work. You can accept all, or choose exactly what
                        you’re comfortable with.
                    </p>
                </div>

                {/* Actions */}
                <div className="px-4 sm:px-6 pb-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                        type="button"
                        onClick={() => setShowCustom((v) => !v)}
                        className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-neutral-300 dark:border-neutral-700 px-4 py-2 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-zinc-800"
                        aria-expanded={showCustom}
                        aria-controls="cookie-customization"
                    >
                        Customization
                    </button>

                    <button
                        type="button"
                        onClick={handleAcceptAll}
                        className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm font-medium hover:opacity-90"
                    >
                        I agree
                    </button>

                    {/* Optional: "Remind me later" simply closes without changing consent */}
                    {showRemindLater && (
                        <button
                            type="button"
                            onClick={handleRemindLater} // <— call the snooze
                            className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-neutral-300 dark:border-neutral-700 px-4 py-2 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-zinc-800"
                        >
                            Remind me later
                        </button>
                    )}
                </div>

                {/* Links */}
                <div className="px-4 sm:px-6 pb-4 text-xs text-neutral-600 dark:text-neutral-400">
                    Learn more in our{" "}
                    <a
                        className="underline hover:no-underline"
                        href={privacyHref}
                    >
                        Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                        className="underline hover:no-underline"
                        href={termsHref}
                    >
                        Terms of Service
                    </a>
                    .
                </div>

                {/* Customization Panel */}
                {showCustom && (
                    <div
                        id="cookie-customization"
                        className="px-4 sm:px-6 pb-6 border-t border-neutral-200 dark:border-neutral-800"
                    >
                        <p className="mt-4 text-sm text-neutral-700 dark:text-neutral-300">
                            Choose which optional cookies we can use. You can
                            change this anytime from the Privacy page. Essential
                            cookies are always on because the site won’t work
                            without them.
                        </p>

                        <div className="mt-4 space-y-4">
                            <ToggleRow
                                title="Essential"
                                description="Required for core features like security, network management, and accessibility. These cannot be switched off."
                                checked={true}
                                disabled
                            />
                            <ToggleRow
                                title="Analytics"
                                description="Helps us understand how the site is used so we can improve performance and content."
                                checked={analytics}
                                onChange={setAnalytics}
                            />
                            <ToggleRow
                                title="Personalization"
                                description="Allows us to tailor recommendations and content to your interests."
                                checked={personalization}
                                onChange={setPersonalization}
                            />
                            <ToggleRow
                                title="Advertising"
                                description="Enables us and our partners to show relevant ads and measure their effectiveness."
                                checked={ads}
                                onChange={setAds}
                            />
                            <ToggleRow
                                title="Preferences"
                                description="Saves your settings (like language or theme) so you don’t have to set them each time."
                                checked={preferences}
                                onChange={setPreferences}
                            />
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleSave}
                                className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm font-medium hover:opacity-90"
                            >
                                Save choices
                            </button>
                            <button
                                type="button"
                                onClick={handleRejectAll}
                                className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-neutral-300 dark:border-neutral-700 px-4 py-2 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-zinc-800"
                            >
                                Reject non‑essential
                            </button>
                            <button
                                type="button"
                                onClick={handleAcceptAll}
                                className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-neutral-300 dark:border-neutral-700 px-4 py-2 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-zinc-800"
                            >
                                Accept all
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ToggleRow({ title, description, checked, onChange, disabled }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="text-sm font-medium">{title}</div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                    {description}
                </div>
            </div>
            <label className="inline-flex items-center cursor-pointer select-none ml-2">
                <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={(e) => onChange?.(e.target.checked)}
                    disabled={disabled}
                    aria-disabled={disabled}
                />
                <span
                    className={[
                        "relative inline-block h-6 w-11 rounded-full transition",
                        disabled
                            ? "bg-neutral-300 dark:bg-neutral-700"
                            : checked
                            ? "bg-black dark:bg-white"
                            : "bg-neutral-300 dark:bg-neutral-700",
                    ].join(" ")}
                    aria-hidden="true"
                >
                    <span
                        className={[
                            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white dark:bg-zinc-900 shadow transform transition",
                            checked ? "translate-x-5" : "",
                        ].join(" ")}
                    />
                </span>
            </label>
        </div>
    );
}
